import { Auth0Client } from '@auth0/auth0-spa-js';
import axios from 'axios';
import store from '../store';
import * as AuthReducer from '../reducers/authReducer';
import { setError } from '../reducers/uiReducer';
import StorageService from './StorageService';
import { LOGIN_SESSION_STORAGE_KEY } from '../constants/AppConstants';

const INTERACTIVE_AUTH_ERRORS = ['mfa_required', 'login_required', 'interaction_required', 'consent_required'];
// How long to assume a loginWithRedirect navigation is in flight before allowing another attempt.
const REDIRECT_FLAG_RESET_MS = 10000;
// The SDK default is 60s. The silent-auth iframe only ever takes that long when it can never
// post back (origin not in Allowed Web Origins, third-party cookies blocked), so fail fast.
const AUTHORIZE_TIMEOUT_SECONDS = 10;
// DELETE /logout is best effort; never let a slow API hold up the Auth0 redirect for long.
const API_LOGOUT_TIMEOUT_MS = 10000;

class AuthService {
  auth0 = null;
  isInitialized = false;
  history = null;
  cachedAccessToken = null; // Cache current token to avoid unnecessary localStorage writes
  isRedirectingForAuth = false;
  forceRefreshPromise = null;
  isLoggingOut = false;
  // Id of the login this tab belongs to (see startLoginSession / adoptLoginSession). Every login,
  // in any tab, writes a fresh id to localStorage, so a mismatch means this tab's login is over.
  tabLoginSessionId = null;

  setHistory = (history) => {
    this.history = history;
  }

  init = async () => {
    if (this.isInitialized) return;

    // createAuth0Client() is `new Auth0Client()` followed by an awaited checkSession(). That check
    // falls back to a hidden authorize iframe when there is no refresh token in the cache, and a
    // non-recoverable failure there (notably a timeout) rejected the whole init and left the app
    // on the loader for up to a minute. Construct the client ourselves and make the check best-effort.
    this.auth0 = new Auth0Client({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      redirect_uri: `${window.location.origin}/callback`,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      authorizeTimeoutInSeconds: AUTHORIZE_TIMEOUT_SECONDS,
    });
    this.isInitialized = true;

    try {
      await this.auth0.checkSession();
    } catch (err) {
      // Not fatal: getTokenSilently on the first request (or loginWithRedirect on /auth) will
      // establish the session. login_required etc. are already swallowed by the SDK here.
      console.warn('Auth0 session check failed, continuing without it:', err?.error || err); // eslint-disable-line no-console
    }

    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      try {
        await this.auth0.handleRedirectCallback();

        const accessToken = await this.auth0.getTokenSilently();
        const idTokenClaims = await this.auth0.getIdTokenClaims();
        const idToken = idTokenClaims?.__raw;

        if (accessToken) {
          this.updateAccessToken(accessToken);
        }
        if (idToken) {
          this.updateIdToken(idToken);
        }

        StorageService.createEntry('authType', 'auth0');

        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        const path = await store.dispatch(AuthReducer.loggedIn());

        // loggedIn() swallows its own errors and resolves with no path. A 401 has already started
        // auth0.logout (see handleLoginError), so step aside. Anything else (5xx or a timeout
        // from /sessionLogin, e.g. during an API deploy) used to fall through to '/', where the
        // auth guard bounced to /auth and straight back into Auth0's live session: an infinite loop.
        if (this.isLoggingOut) return;
        if (!path && !StorageService.readEntry('user')) {
          console.error('Session login did not complete; sending user back to /auth');
          this.clearSession();
          window.location.replace('/auth?logout_message=api_unavailable');
          return;
        }

        if (this.history) {
          this.history.push(path || '/');
        } else {
          window.location.href = path || '/';
        }
      } catch (err) {
        console.error('Error handling redirect callback:', err);
        store.dispatch(setError(err));

        // Clear the half-built session so the next load does not try to log out again,
        // then land on /auth where the message is shown instead of auto-redirecting.
        await this.apiLogout();
        this.clearSession();

        window.location.href = '/auth?logout_message=auth0error';
      }
    } else if (window.location.search.includes('error=')) {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      window.history.replaceState({}, document.title, window.location.pathname);

      let logoutMessage = 'auth0error';
      if (error === 'access_denied') {
        logoutMessage = 'access_denied';
      } else if (error === 'unauthorized') {
        logoutMessage = 'user_blocked';
      } else if (errorDescription?.toLowerCase().includes('blocked')) {
        logoutMessage = 'user_blocked';
      } else if (error === 'login_required') {
        logoutMessage = 'login_required';
      } else if (error === 'consent_required') {
        logoutMessage = 'consent_required';
      }

      await this.apiLogout();
      this.clearSession();
      // Only Auth0 redirects carry `error=`, so these are Auth0 users: show the message on /auth
      // (which renders a Log In button when a message is present) rather than the legacy form.
      window.location.replace(`/auth?logout_message=${logoutMessage}`);
    } else {
      // Load cached token on init
      const token = localStorage.getItem('access_token');
      if (token) {
        this.cachedAccessToken = token;
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
    }
  }

  /**
   * Start the Auth0 redirect login.
   * @param {string} [errorMessage] forwarded to Auth0 as `logout_message` for the Universal Login template
   * @param {object} [options]
   * @param {boolean} [options.forceLogin] send `prompt=login` so Auth0 shows the credential form even
   *   when it still holds a session. Used when retrying from an error page: after a post-login
   *   denial Auth0 keeps the session for the denied account, so without this the retry silently
   *   re-runs the same denial and the user can never switch accounts.
   */
  login = async (errorMessage, { forceLogin = false } = {}) => {
    await this.init();
    let connection = StorageService.readEntry('auth-connection');
    connection = ((connection !== 'all') ? connection : undefined);

    if (!this.auth0) throw new Error('Auth0 client not initialized');

    return this.auth0.loginWithRedirect({
      redirect_uri: `${window.location.origin}/callback`,
      scope: 'openid profile email offline_access',
      ...(connection && { connection }),
      ...(errorMessage && { logout_message: errorMessage }),
      ...(forceLogin && { prompt: 'login' }),
    });
  }

  getAccessToken = async () => {
    await this.init();
    try {
      // The SDK checks expiry, refreshes with the refresh token when needed, dedupes concurrent
      // calls and holds a cross-tab lock, so this is cheap on the cached path.
      const token = await this.auth0.getTokenSilently();

      // Only touch storage (and only read the id token) when the access token actually rolled over.
      if (this.updateAccessToken(token)) {
        await this.syncIdToken();
      }

      return token;
    } catch (err) {
      return this.handleTokenError(err);
    }
  }

  /**
   * Bypass the SDK cache and hit the token endpoint with the refresh token. Used by the 401 retry,
   * where the cached token is by definition the one the API just rejected. Concurrent callers
   * share one in-flight refresh so a burst of 401s does not become a burst of refreshes.
   */
  forceRefresh = async () => {
    await this.init();
    if (!this.forceRefreshPromise) {
      this.forceRefreshPromise = this.auth0.getTokenSilently({ ignoreCache: true })
        .then(async (token) => {
          if (this.updateAccessToken(token)) {
            await this.syncIdToken();
          }
          return token;
        })
        .catch((err) => this.handleTokenError(err))
        .finally(() => {
          this.forceRefreshPromise = null;
        });
    }
    return this.forceRefreshPromise;
  }

  syncIdToken = async () => {
    const idTokenClaims = await this.auth0.getIdTokenClaims();
    const idToken = idTokenClaims?.__raw;
    if (idToken) {
      this.updateIdToken(idToken);
    }
  }

  /**
   * Interactive errors (mfa_required, login_required, ...) cannot be resolved silently: send the
   * user through the normal login entry point (so `connection` is applied consistently) and
   * resolve null so callers treat it as "no token". Anything else is rethrown.
   */
  handleTokenError = (err) => {
    if (INTERACTIVE_AUTH_ERRORS.includes(err.error)) {
      if (!this.isRedirectingForAuth) {
        this.isRedirectingForAuth = true;
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (!currentPath.includes('/callback') && !currentPath.includes('/auth') && !currentPath.includes('/login')) {
          sessionStorage.setItem('redirect_url', currentPath);
        }

        this.login()
          .catch((loginErr) => console.error('Login redirect failed:', loginErr))
          .finally(() => {
            // loginWithRedirect normally navigates away. If it did not (blocked, offline), release
            // the flag so a later request can try again instead of silently returning null forever.
            setTimeout(() => { this.isRedirectingForAuth = false; }, REDIRECT_FLAG_RESET_MS);
          });
      }
      return null;
    }
    console.error('Error getting access token:', err);
    throw err;
  }

  getIdToken = async () => {
    await this.init();
    return (await this.auth0.getIdTokenClaims())?.__raw;
  }

  /**
   * Update access token in localStorage only if it has changed
   * @param {string} newToken - The new access token
   */
  updateAccessToken(newToken) {
    if (newToken && newToken !== this.cachedAccessToken) {
      localStorage.setItem('access_token', newToken);
      this.cachedAccessToken = newToken;
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      return true;
    }
    return false;
  }

  /**
   * Update ID token in localStorage only if it has changed
   * @param {string} newToken - The new ID token
   */
  updateIdToken(newToken) {
    const currentIdToken = localStorage.getItem('id_token');
    if (newToken && newToken !== currentIdToken) {
      localStorage.setItem('id_token', newToken);
      return true;
    }
    return false;
  }

  /**
   * Clear all tokens from cache and storage
   */
  clearTokens() {
    this.cachedAccessToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('authType');
    delete axios.defaults.headers.common.Authorization;
  }

  /** A new login in this tab: write a fresh id for every tab to compare against, and own it. */
  startLoginSession() {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    StorageService.createEntry(LOGIN_SESSION_STORAGE_KEY, id);
    this.tabLoginSessionId = id;
  }

  /** This tab loaded into an existing login (page load or refresh): own whatever id is current. */
  adoptLoginSession() {
    this.tabLoginSessionId = StorageService.readEntry(LOGIN_SESSION_STORAGE_KEY) || null;
  }

  /**
   * False once this tab's login has ended: logged out anywhere, or replaced by a new login (even of
   * the same user) in another tab. A tab with no id (logged in before this existed) gets true, so
   * callers keep their previous behavior.
   */
  isTabLoginSessionCurrent() {
    if (!this.tabLoginSessionId) return true;
    return this.tabLoginSessionId === StorageService.readEntry(LOGIN_SESSION_STORAGE_KEY);
  }

  /**
   * Clear tokens plus the app-level session markers (user/org/authType/...).
   * Use this on every error path that sends the user back to /auth; clearing only the tokens
   * leaves `user` behind and App.getInitialView then dispatches another logout on the next load.
   */
  clearSession() {
    this.clearTokens();
    this.tabLoginSessionId = null;
    ['user', 'org', 'authType', 'multiOrgs', 'patientExperience', 'subscriptions', LOGIN_SESSION_STORAGE_KEY].forEach((key) => StorageService.destroyEntry(key));
    ['redirect_url', 'axiumLogin', 'activePatientId'].forEach((key) => sessionStorage.removeItem(key));
  }

  /**
   * Best-effort DELETE /logout so the API destroys the session row and clears connect.sid.
   * Sent with _skipAuth so an expired token neither blocks on a refresh nor starts another logout;
   * the API accepts the call on the session cookie alone. Never throws.
   */
  apiLogout = async (url = '/logout') => {
    try {
      await axios.delete(url, { _skipAuth: true, timeout: API_LOGOUT_TIMEOUT_MS });
    } catch (err) {
      console.error('API logout failed:', err?.response || err); // eslint-disable-line no-console
    }
  }

  /**
   * End the session everywhere: API session (unless the caller already did), local storage, Auth0.
   * @param {string} [logoutType] shown on /auth after Auth0 returns
   * @param {object} [options]
   * @param {boolean} [options.apiLogout=true] false when the caller already called DELETE /logout
   */
  logout = async (logoutType, { apiLogout = true } = {}) => {
    this.isLoggingOut = true;
    try {
      if (apiLogout) await this.apiLogout();
      await this.init();

      // Everything, not just tokens: leaving `user`/`org` behind is how a later load re-entered
      // the app as the previous account.
      this.clearSession();

      let params = '';
      if (logoutType) params = `?logout_message=${logoutType}`;

      if (!this.auth0) {
        console.error('Auth0 client not initialized, redirecting manually');
        window.location.href = `${window.location.origin}/auth${params}`;
        return;
      }

      await this.auth0.logout({
        returnTo: `${window.location.origin}/auth${params}`,
      });
    } catch (err) {
      console.error('Logout error:', err);
      this.clearSession();

      let params = '';
      if (logoutType) params = `?logout_message=${logoutType}`;
      window.location.href = `${window.location.origin}/auth${params}`;
    }
  }
}

export default new AuthService();
